import React, { useState, useEffect } from 'react';
import { usePlayer } from '../../lib/PlayerContext';
import { getArtistNames } from '../../utils/artistUtils';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import RepeatIcon from '@mui/icons-material/Repeat';
import RepeatOneIcon from '@mui/icons-material/RepeatOne';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import LyricsIcon from '@mui/icons-material/Lyrics';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import { useQuery } from '@tanstack/react-query';
import { fetchAllData } from '../../lib/api';
import { LyricsOverlay } from '../LyricsOverlay';

interface FullScreenPlayerProps {
  isOpen: boolean;
  onClose: () => void;
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function FullScreenPlayer({ isOpen, onClose }: FullScreenPlayerProps) {
  const player = usePlayer();
  const { data } = useQuery(['appData'], fetchAllData);
  const progressBarRef = React.useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragProgress, setDragProgress] = React.useState<number | null>(null);
  const [showLyrics, setShowLyrics] = React.useState(false);
  const [showQueue, setShowQueue] = React.useState(false);
  const [isFavorite, setIsFavorite] = React.useState(false);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    player.setVolume(newVolume);
  };

  const updateProgressFromMouse = (clientX: number) => {
    if (!progressBarRef.current) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const newProgress = Math.max(0, Math.min(1, x / rect.width));
    setDragProgress(newProgress);
  };

  const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    updateProgressFromMouse(e.clientX);
  };

  const handleProgressMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    updateProgressFromMouse(e.clientX);
  };

  const handleProgressMouseUp = () => {
    if (!isDragging || dragProgress === null) return;
    player.seek(dragProgress);
    setIsDragging(false);
    setDragProgress(null);
  };

  React.useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleProgressMouseUp();
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragging, dragProgress]);

  const getArtistName = (artistId: string) => {
    if (!data) return 'Unknown Artist';
    return getArtistNames(artistId, data.artists);
  };

  const getVoicebankName = (synthId: string) => {
    if (!data) return 'Unknown Voicebank';
    return data.voicebanks.find(v => v.id === synthId)?.name || 'Unknown Voicebank';
  };

  const displayProgress = isDragging ? (dragProgress ?? player.progress) : player.progress;

  if (!isOpen || !player.currentSong) return null;

  return (
    <div className="fixed inset-0 bg-true-black z-50 overflow-hidden">
      {/* Dynamic Background */}
      <div className="absolute inset-0">
        {/* Blurred album cover background */}
        <div 
          className="absolute inset-0 bg-cover bg-center filter blur-3xl opacity-20"
          style={{ backgroundImage: `url(${player.currentSong.coverUrl})` }}
        />
        
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-true-black/50 to-true-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-true-black/80 via-transparent to-true-black/80" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <button
            onClick={onClose}
            className="p-3 text-text-secondary hover:text-pearl-white hover:bg-white/10 rounded-full transition-all duration-200 elevation-1"
            aria-label="Exit full screen"
          >
            <FullscreenExitIcon sx={{ fontSize: 28 }} />
          </button>
          
          <h2 className="text-pearl-white font-outfit text-lg font-medium">Now Playing</h2>
          
          <button
            onClick={() => setShowQueue(!showQueue)}
            className="p-3 text-text-secondary hover:text-pearl-white hover:bg-white/10 rounded-full transition-all duration-200 elevation-1"
            aria-label="Toggle queue"
          >
            <QueueMusicIcon sx={{ fontSize: 28 }} />
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 pb-32">
          
          {/* Album Art */}
          <div className="relative mb-8 group">
            <div className="w-80 h-80 sm:w-96 sm:h-96 rounded-2xl overflow-hidden elevation-5 pink-shimmer">
              <img 
                src={player.currentSong.coverUrl}
                alt={player.currentSong.title}
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Floating controls overlay */}
            <div className="absolute inset-0 bg-true-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl flex items-center justify-center">
              <button
                onClick={() => setIsFavorite(!isFavorite)}
                className="p-4 text-pearl-white hover:text-naia-pink transition-all duration-200 hover:scale-110"
              >
                {isFavorite ? <FavoriteIcon sx={{ fontSize: 32 }} /> : <FavoriteBorderIcon sx={{ fontSize: 32 }} />}
              </button>
            </div>
          </div>

          {/* Song Info */}
          <div className="text-center mb-8 max-w-2xl">
            <h1 className="text-4xl sm:text-5xl font-outfit font-bold text-pearl-white mb-4 leading-tight">
              {player.currentSong.title}
            </h1>
            <p className="text-xl text-text-secondary mb-2">
              {getArtistName(player.currentSong.artistId)}
            </p>
            <p className="text-lg text-text-muted">
              {getVoicebankName(player.currentSong.synthId)}
            </p>
          </div>

          {/* Progress Bar */}
          <div className="w-full max-w-2xl mb-8">
            <div 
              ref={progressBarRef}
              className="relative w-full h-2 bg-white/10 rounded-full cursor-pointer group"
              onMouseDown={handleProgressMouseDown}
              onMouseMove={handleProgressMouseMove}
              onMouseUp={handleProgressMouseUp}
            >
              <div 
                className="absolute h-full bg-gradient-to-r from-naia-pink to-soft-violet rounded-full transition-all duration-200"
                style={{ 
                  width: `${displayProgress * 100}%`,
                  transition: isDragging ? 'none' : 'width 0.1s linear'
                }}
              />
              <div 
                className="absolute top-1/2 -mt-3 -ml-3 w-6 h-6 rounded-full bg-pearl-white opacity-0 group-hover:opacity-100 transition-opacity shadow-lg elevation-2"
                style={{ 
                  left: `${displayProgress * 100}%`,
                  display: isDragging || player.progress > 0 ? 'block' : 'none'
                }}
              />
            </div>
            
            {/* Time display */}
            <div className="flex justify-between items-center mt-3 text-text-muted text-sm">
              <span>{formatTime(displayProgress * player.duration)}</span>
              <span>{formatTime(player.duration)}</span>
            </div>
          </div>

          {/* Main Controls */}
          <div className="flex items-center justify-center space-x-6 mb-8">
            <button 
              className="p-3 text-text-secondary hover:text-pearl-white transition-all duration-300 hover:scale-110 elevation-1 hover:elevation-2"
              onClick={player.shuffleQueue}
            >
              <ShuffleIcon 
                sx={{ fontSize: 32 }} 
                className={player.isShuffled ? 'text-naia-pink' : ''} 
              />
            </button>
            
            <button 
              className="p-4 text-pearl-white hover:text-naia-pink transition-all duration-300 hover:scale-110 elevation-2"
              onClick={player.playPrevious}
            >
              <SkipPreviousIcon sx={{ fontSize: 40 }} />
            </button>
            
            <button 
              className="p-6 bg-gradient-to-r from-naia-pink to-soft-violet rounded-full text-pearl-white hover:scale-110 transition-all duration-300 elevation-3 naia-glow"
              onClick={player.togglePlayPause}
            >
              {player.state === 'playing' ? (
                <PauseIcon sx={{ fontSize: 48 }} />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 48 }} />
              )}
            </button>
            
            <button 
              className="p-4 text-pearl-white hover:text-naia-pink transition-all duration-300 hover:scale-110 elevation-2"
              onClick={player.playNext}
            >
              <SkipNextIcon sx={{ fontSize: 40 }} />
            </button>
            
            <button 
              className="p-3 text-text-secondary hover:text-pearl-white transition-all duration-300 hover:scale-110 elevation-1 hover:elevation-2"
              onClick={player.toggleRepeat}
            >
              {player.repeatMode === 'one' ? (
                <RepeatOneIcon sx={{ fontSize: 32 }} className="text-naia-pink" />
              ) : (
                <RepeatIcon 
                  sx={{ fontSize: 32 }} 
                  className={player.repeatMode !== 'none' ? 'text-naia-pink' : ''} 
                />
              )}
            </button>
          </div>

          {/* Secondary Controls */}
          <div className="flex items-center space-x-8">
            {/* Volume Control */}
            <div className="flex items-center space-x-3">
              <VolumeUpIcon className="text-text-muted" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={player.volume}
                onChange={handleVolumeChange}
                className="w-24"
              />
              <span className="text-text-muted text-sm w-8">
                {Math.round(player.volume * 100)}%
              </span>
            </div>

            {/* Lyrics Button */}
            {player.currentSong?.lrcUrl && (
              <button
                onClick={() => setShowLyrics(true)}
                className="p-3 text-text-secondary hover:text-pearl-white hover:bg-white/10 rounded-full transition-all duration-200 elevation-1"
                aria-label="Show lyrics"
              >
                <LyricsIcon sx={{ fontSize: 24 }} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lyrics Overlay */}
      {showLyrics && player.currentSong && (
        <LyricsOverlay
          song={player.currentSong}
          currentTime={player.progress * player.duration}
          isVisible={showLyrics}
          onClose={() => setShowLyrics(false)}
        />
      )}

      {/* Queue Sidebar */}
      {showQueue && (
        <div className="absolute right-0 top-0 h-full w-80 bg-surface-dark/95 backdrop-blur-lg border-l border-surface-border p-6 transform transition-transform duration-300 elevation-4">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-pearl-white font-outfit font-medium text-lg">Queue</h3>
            <span className="text-text-muted text-sm">{player.queue.length} songs</span>
          </div>
          
          <div className="space-y-3 overflow-y-auto max-h-full">
            {player.queue.map((song, index) => (
              <div 
                key={`${song.id}-${index}`}
                className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                  song.id === player.currentSong?.id 
                    ? 'bg-naia-pink/20 border border-naia-pink/30' 
                    : 'hover:bg-white/5'
                }`}
              >
                <img 
                  src={song.coverUrl} 
                  alt={song.title}
                  className="w-10 h-10 rounded-lg object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-pearl-white text-sm font-medium truncate">{song.title}</p>
                  <p className="text-text-muted text-xs truncate">
                    {getArtistName(song.artistId)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
} 